import { Quill } from '@vueup/vue-quill';
import type { TypedUser } from '@models/typedUser.ts';
import { eventBus } from '@tools/event-bus';
import { UserMentionBlot } from './userMentionBlot.ts';

Quill.register({ 'formats/user-mention': UserMentionBlot }, true);

export const QUILL_USER_MENTION_EVENT_SEARCH = 'quill-user-mention-search';
export const QUILL_USER_MENTION_EVENT_EXIT = 'quill-user-mention-exit';
export const QUILL_USER_MENTION_EVENT_SELECT = 'quill-user-mention-select';
export const QUILL_USER_MENTION_EVENT_DOWN = 'quill-user-mention-down';
export const QUILL_USER_MENTION_EVENT_UP = 'quill-user-mention-up';

export type UserMentionModuleApi = {
  selectUser: (user: TypedUser) => void;
};

export class UserMentionModule implements UserMentionModuleApi {
  private readonly quill: Quill;

  private startIndex: number | null = null;

  private endIndex: number | null = null;

  constructor(quill: Quill) {
    this.quill = quill;
    this.initialize();
  }

  private initialize = (): void => {
    this.handleTyping();
  };

  private handleTyping = (): void => {
    this.quill.on('text-change', (_delta: unknown, _oldDelta: unknown, source: unknown) => {
      if (source !== 'user') {
        return;
      }

      const range = this.quill.getSelection();
      if (!range) {
        return;
      }

      const textBeforeCursor = this.quill.getText(0, range.index);
      const words = textBeforeCursor.split(/\s+/);
      const currentWord = words[words.length - 1];

      if (currentWord === '@') {
        this.startIndex = range.index - 1;
      }

      if (this.startIndex !== null) {
        this.endIndex = range.index;
        if (this.endIndex > this.startIndex) {
          this.handleMention();
          return;
        }
      }
      this.exitMention();
    });
  };

  private handleMention = (): void => {
    if (this.endIndex === null || this.startIndex === null) {
      throw new Error('Indexes cannot be null');
    }

    const length = this.endIndex - this.startIndex;
    const search = this.quill.getText(this.startIndex, length);

    eventBus.dispatchEvent(new CustomEvent(QUILL_USER_MENTION_EVENT_SEARCH, { detail: { search } }));
    this.quill.root.addEventListener('keydown', this.handleKeyboardEvents, true);
  };

  private handleKeyboardEvents = (e: KeyboardEvent): boolean => {
    if (![ 'Escape', 'Tab', 'Enter', 'ArrowUp', 'ArrowDown' ].includes(e.key)) {
      return true;
    }

    e.preventDefault();
    e.stopPropagation();

    switch (e.key) {
      case 'Escape':
        this.exitMention();
        break;
      case 'Tab':
      case 'Enter':
        eventBus.dispatchEvent(new CustomEvent(QUILL_USER_MENTION_EVENT_SELECT));
        this.quill.root.removeEventListener('keydown', this.handleKeyboardEvents, true);
        break;
      case 'ArrowUp':
        eventBus.dispatchEvent(new CustomEvent(QUILL_USER_MENTION_EVENT_UP));
        break;
      case 'ArrowDown':
        eventBus.dispatchEvent(new CustomEvent(QUILL_USER_MENTION_EVENT_DOWN));
        break;
      default:
        break;
    }
    return false;
  };

  private exitMention = (): void => {
    this.startIndex = null;
    this.endIndex = null;
    eventBus.dispatchEvent(new CustomEvent(QUILL_USER_MENTION_EVENT_EXIT));
    this.quill.root.removeEventListener('keydown', this.handleKeyboardEvents, true);
  };

  public selectUser = (user: TypedUser): void => {
    const range = this.quill.getSelection(true);
    if (!range) {
      this.exitMention();
      return;
    }

    const from = this.startIndex;
    const
      to = this.endIndex;
    if (from === null || to === null) {
      throw new Error('Indexes cannot be null');
    }

    const len = Math.max(0, to - from);
    if (len > 0) {
      this.quill.deleteText(from, len, 'user');
    }

    const textContent = `@${user.firstname} ${user.lastname.toUpperCase()}`;

    this.quill.insertEmbed(from, UserMentionBlot.blotName, {
      id: String(user.id),
      type: String(user.type),
      text: textContent,
    }, 'user');

    this.quill.insertText(from + 1, ' ', 'user');
    this.quill.setSelection(from + 2, 0);

    this.exitMention();
  };
}
